import { ITariff } from "../tariff/tariff.types"
import { ITariffBundle, IBundleAnnualCost } from "./tariffBundle.types"
import { IUsageEstimation } from "@/services/UsageEstimationService/types"
import tariffHelper from "@/helpers/tariffHelper"

export class TariffBundle implements ITariffBundle {
  tariffs: ITariff[]

  constructor(tariffs: ITariff[]) {
    this.tariffs = tariffs
  }

  get name(): string {
    return this.tariffs[0].display.split(" -")[0]
  }
  get code(): string {
    return tariffHelper.getBundleCode(this.tariffs[0].code)
  }
  get type(): string {
    return this.tariffs[0].productDetails.tariffComparisonCode
  }
  get duration(): number | null {
    return this.tariffs[0].durationMonths
  }
  get exitFee(): number {
    return this.tariffs[0].productDetails.earlyTerminationCharge
  }
  // Electricity exit fees follow the electricity VAT rate while gas stays at
  // 5%, so a bundle's two fuels can carry different fees.
  get exitFees(): { elec?: number; gas?: number } {
    return {
      elec: (this.elec1Rate ?? this.elec2Rate)?.productDetails
        .earlyTerminationCharge,
      gas: this.gas?.productDetails.earlyTerminationCharge,
    }
  }
  get hasDirectDebit(): boolean {
    return this.tariffs.some((tariff) => tariff.directDebit)
  }
  get isNSC(): boolean {
    return this.tariffs.every((tariff) => tariff.code.includes("NSC"))
  }

  get elec1Rate(): ITariff | undefined {
    return this.tariffs.find((tariff) => tariff.isOneRate && !tariff.isGas)
  }
  get elec2Rate(): ITariff | undefined {
    return this.tariffs.find((tariff) => !tariff.isOneRate && !tariff.isGas)
  }
  get gas(): ITariff | undefined {
    return this.tariffs.find((tariff) => tariff.isGas)
  }

  public annualCost({
    usage,
    isElectricity,
    isTwoRate = false,
  }: IBundleAnnualCost) {
    if (isElectricity === undefined) {
      return this.calculateAnnualCost(usage)
    } else {
      return this?.calculateAnnualCost(
        isElectricity
          ? isTwoRate
            ? {
                electricityDay: usage.electricityDay,
                electricityNight: usage.electricityNight,
              }
            : { electricity: usage.electricity }
          : { gas: usage.gas },
      )
    }
  }

  private calculateAnnualCost(usage: Partial<IUsageEstimation>) {
    let annualCost = 0
    if (this.gas && typeof usage.gas === "number") {
      annualCost += tariffHelper.calculateBasicAnnualCost(this.gas, usage)
    }
    if (this.elec1Rate && typeof usage.electricity === "number") {
      annualCost += tariffHelper.calculateBasicAnnualCost(this.elec1Rate, usage)
    }
    if (
      this.elec2Rate &&
      (typeof usage.electricityDay === "number" ||
        typeof usage.electricityNight === "number")
    ) {
      annualCost += tariffHelper.calculateBasicAnnualCost(this.elec2Rate, usage)
    } else if (
      this.elec1Rate &&
      (typeof usage.electricityDay === "number" ||
        typeof usage.electricityNight === "number")
    ) {
      annualCost += tariffHelper.calculateBasicAnnualCost(this.elec1Rate, usage)
    }
    return annualCost
  }
}
